import { Alert, SpinningLoader } from '@weave/design-system';
import { useGetHandoffHubSnapshotByIdQuery } from '../../handoff-hub.queries';
import { useLocation, useParams } from 'react-router-dom';
import {
  DeprecatedHandoffTypes,
  HandoffTypes,
} from './edit-handoff/handoff-metrics/handoff.constants';
import { SnapshotViewComponent } from './view-handoff/snapshot-view.component';
import { VoiceAirSnapshotView } from './view-handoff/voice-air-snapshot-view.component';
import { MessagingAirSnapshotView } from './view-handoff/messaging-air-snapshot-view.component';
import { WieSnapshotView } from './view-handoff/wie-snapshot-view.component';
import { UnifySnapshotView } from './view-handoff/unify-snapshot-view.component';
import { ONBOARDING, injectParams } from '../../../../../constants/paths';
import { Page } from '../../../../../components/shared';
import { AddEditHandoff } from './edit-handoff/add-edit-handoff';
import { FC } from 'react';
import { HandoffSnapshot } from '../../handoff-hub.types';
import { useSelector } from 'react-redux';
import { selectCurrentLocationId } from '../../../../../redux/actions/location';

const ViewHandoffPage: FC = ({ children }) => {
  const { id: locationId } = useParams<{ id: string }>();
  const title = 'View Handoff';
  const handoffHubUrl = injectParams(ONBOARDING.handoff, { id: locationId });
  return (
    <Page
      title={title}
      breadcrumbs={[
        { url: handoffHubUrl, title: 'Handoff Hub' },
        { url: '', title: title },
      ]}
    >
      {children}
    </Page>
  );
};

interface ReadOnlyViewProps {
  snapshot: HandoffSnapshot;
  locationId: string;
  groupId?: string;
}

// Types with a dedicated read-only view; everything else uses the legacy snapshot view.
const readOnlyViews: Partial<Record<HandoffTypes, FC<ReadOnlyViewProps>>> = {
  [HandoffTypes.VoiceAirHandoff]: ({ snapshot, locationId }) => (
    <VoiceAirSnapshotView snapshotData={snapshot} locationId={locationId} />
  ),
  [HandoffTypes.MessagingAirHandoff]: ({ snapshot, locationId }) => (
    <MessagingAirSnapshotView snapshotData={snapshot} locationId={locationId} />
  ),
  [HandoffTypes.WeaveInsuranceEligibility]: ({ snapshot }) => (
    <WieSnapshotView snapshotData={snapshot} />
  ),
  [HandoffTypes.OnboardingHandoffUnify]: ({ snapshot, groupId }) =>
    groupId ? (
      <UnifySnapshotView groupId={groupId} />
    ) : (
      // Fallback to the legacy snapshot view if no groupId is available
      <SnapshotViewComponent snapshotData={snapshot} />
    ),
};

export const HandoffHubInfo = () => {
  const { snapshotId } = useParams<{ id: string; snapshotId: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryGroupId = queryParams.get('groupId');
  const locationId = useSelector(selectCurrentLocationId);

  const {
    data: snapshotInfo,
    isError,
    isLoading,
  } = useGetHandoffHubSnapshotByIdQuery(snapshotId);

  if (isLoading || !locationId) {
    return (
      <ViewHandoffPage>
        <SpinningLoader className="loader" size="medium" />
      </ViewHandoffPage>
    );
  }
  if (isError) {
    return (
      <ViewHandoffPage>
        <Alert type="error">
          There was an error fetching the handoff notes. Please try again.
        </Alert>
      </ViewHandoffPage>
    );
  }
  if (!snapshotInfo) {
    return (
      <ViewHandoffPage>
        <Alert type="error">Handoff details not found</Alert>
      </ViewHandoffPage>
    );
  }

  const isEditable = snapshotInfo.submittedAt === null;
  const isDeprecated = Object.values(DeprecatedHandoffTypes).includes(
    snapshotInfo.handoffType as DeprecatedHandoffTypes
  );

  const effectiveGroupId = queryGroupId || snapshotInfo.groupId;
  const snapshotWithGroup = effectiveGroupId
    ? { ...snapshotInfo, groupId: effectiveGroupId }
    : snapshotInfo;

  if (isEditable && !isDeprecated) {
    return <AddEditHandoff handoffSnapshot={snapshotWithGroup} />;
  }

  const ReadOnlyView = readOnlyViews[snapshotInfo.handoffType as HandoffTypes];
  if (ReadOnlyView) {
    return (
      <ViewHandoffPage>
        <ReadOnlyView
          snapshot={snapshotInfo}
          locationId={locationId}
          groupId={effectiveGroupId}
        />
      </ViewHandoffPage>
    );
  }

  return (
    <ViewHandoffPage>
      {isEditable && isDeprecated && (
        <Alert type="warning">
          This handoff type has been deprecated and can no longer be edited.
        </Alert>
      )}
      <SnapshotViewComponent snapshotData={snapshotInfo} />
    </ViewHandoffPage>
  );
};
